import { type Timestamp, timestampDate } from '@bufbuild/protobuf/wkt';
import { type MeteringItem } from '@qdrant/qdrant-cloud-public-api/metering/v1';
import { type Card, type PaymentMethod, PaymentProviderType } from '@qdrant/qdrant-cloud-public-api/payment/v1';
import { FRACTIONAL_TO_BASIC_MONETARY_UNIT_FACTOR } from '@/components/Clusters/ClusterSetup/helpers/constants';
import { AVG_DAYS_PER_MONTH, HOURS_IN_A_DAY } from '@/components/Clusters/constants';
import { FORMAT_LOCALE } from '@/utils/constants';
import { type PaymentMethodProviderId, type PaymentMethodStripe } from './types';

const isBillingAddressExplicit = (paymentMethod: PaymentMethod) =>
  isPaymentMethodStripe(paymentMethod) && paymentMethod.billingAddress !== undefined;

const isBillingAddressImplicit = (paymentMethod: PaymentMethod) =>
  isPaymentMethodMarketplace(paymentMethod) || isPaymentMethodCustom(paymentMethod);

export const isPaymentMethodCustom = (paymentMethod: PaymentMethod): paymentMethod is PaymentMethodStripe =>
  paymentMethod.type === PaymentProviderType.CUSTOM;

export const isPaymentMethodStripe = (paymentMethod: PaymentMethod): paymentMethod is PaymentMethodStripe =>
  paymentMethod.type === PaymentProviderType.STRIPE;

export const isPaymentMethodMarketplace = (paymentMethod: PaymentMethod) =>
  [
    PaymentProviderType.AWS_MARKETPLACE,
    PaymentProviderType.GCP_MARKETPLACE,
    PaymentProviderType.AZURE_MARKETPLACE,
  ].includes(paymentMethod.type);

export const isBillingInfoProvided = (paymentMethod: PaymentMethod): boolean => {
  if (paymentMethod.type !== PaymentProviderType.UNSPECIFIED) {
    return isBillingAddressImplicit(paymentMethod) || isBillingAddressExplicit(paymentMethod);
  }
  return false;
};

export const isCardInfoProvided = (
  paymentMethod: PaymentMethod,
): paymentMethod is PaymentMethodStripe & { paymentMethodDetails: { card: Card } } =>
  Boolean(paymentMethod.paymentMethodDetails?.card);

export const isBillingTaxSupportedCountry = (paymentMethod: PaymentMethod) =>
  isBillingAddressExplicit(paymentMethod) && paymentMethod.billingAddress?.taxSupportedCountry;

/**
 * Returns a formatted year-month string like "Jan 2024"
 */
export const formatYearMonthUTC = (year: number, month: number) =>
  new Date(Date.UTC(year, month - 1)).toLocaleDateString(FORMAT_LOCALE, {
    year: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

/**
 * Returns a formatted date string like "MM/DD/YYYY"
 */
export const formatTimestampUTC = (timestamp: Timestamp) =>
  timestampDate(timestamp).toLocaleTimeString(FORMAT_LOCALE, {
    timeZone: 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

const currencyFormatter = new Intl.NumberFormat(FORMAT_LOCALE, {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  // `halfExpand` is the default, just to avoid confusion by leaving it empty
  roundingMode: 'halfExpand',
});

const currencyFormatterFractional = new Intl.NumberFormat(FORMAT_LOCALE, {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
  // `halfExpand` is the default, just to avoid confusion by leaving it empty
  roundingMode: 'halfExpand',
});

const monthlyPriceFormatter = new Intl.NumberFormat(FORMAT_LOCALE, {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  // `halfExpand` is the default, just to avoid confusion by leaving it empty
  roundingMode: 'halfExpand',
});

/**
 * Formats a price delta without trailing ".00" — shows decimals only when non-zero.
 * e.g. 120 → "$120", 120.5 → "$120.50"
 * Intended for compact price badges ("+$X/node"), not for invoice totals.
 */
const priceTagDeltaFormatter = new Intl.NumberFormat(FORMAT_LOCALE, {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
  roundingMode: 'halfExpand',
});

/**
 * Returns a currency string in the format of $0.00
 * @param value - amount with cents as fraction digits (already divided by 100), fixed 2 fractions
 * @returns string
 */
export const toFormattedCurrency = (value: number) => currencyFormatter.format(value);

toFormattedCurrency.fromMillicents = (value: bigint) =>
  toFormattedCurrency(Number(value) / FRACTIONAL_TO_BASIC_MONETARY_UNIT_FACTOR);

/**
 * Returns a currency string in the format of $0.00
 * @param value - amount with cents as fraction digits (already divided by 100), 2-4 factions
 * @returns string
 */
export const toFormattedCurrencyFractional = (value: number) => currencyFormatterFractional.format(value);

toFormattedCurrencyFractional.fromMillicents = (value: bigint) =>
  toFormattedCurrencyFractional(Number(value) / FRACTIONAL_TO_BASIC_MONETARY_UNIT_FACTOR);

/** Credit amounts arrive as floats; millicents are the exact unit usage is already counted in. */
export const creditsToMillicents = (credits: number): bigint =>
  BigInt(Math.round(credits * FRACTIONAL_TO_BASIC_MONETARY_UNIT_FACTOR));

/**
 * Returns a monthly price string with exactly 2 decimal places (e.g., $123.45)
 * Used for monthly pricing where high precision is not needed
 * @param value - amount in dollars
 * @returns string
 */

export const toFormattedMonthlyPrice = (value: bigint | number) => monthlyPriceFormatter.format(value);

export const millicentsToMonthlyPrice = (millicents: bigint | number): number =>
  (Number(millicents) / FRACTIONAL_TO_BASIC_MONETARY_UNIT_FACTOR) * HOURS_IN_A_DAY * AVG_DAYS_PER_MONTH;

export const toFormattedPriceTagDelta = (value: number) => priceTagDeltaFormatter.format(value);

export const formatDiscount = (discountMillicents?: bigint) => {
  if (discountMillicents === undefined || discountMillicents === 0n) {
    return '-';
  }

  return toFormattedCurrency.fromMillicents(discountMillicents);
};

export const hasDiscountWithinMeteringItems = (items: MeteringItem[]) =>
  items.some(
    (item) =>
      Boolean(item.discountAmountMillicents && item.discountAmountMillicents > 0) ||
      Boolean(item.discountAmountPercent && item.discountAmountPercent > 0),
  );

export const paymentProviderTypes: PaymentMethodProviderId[] = ['stripe', 'aws', 'gcp', 'azure'] as const;

export const paymentProviderTypeMap = new Map<PaymentProviderType, PaymentMethodProviderId>([
  [PaymentProviderType.STRIPE, 'stripe'],
  [PaymentProviderType.AWS_MARKETPLACE, 'aws'],
  [PaymentProviderType.GCP_MARKETPLACE, 'gcp'],
  [PaymentProviderType.AZURE_MARKETPLACE, 'azure'],
]);
